import { type FormEvent, type ReactNode, useEffect, useState } from "react";

import { Textarea } from "@virtual-brain-lib/frontend-design-system/ui";
import { cn } from "@virtual-brain-lib/frontend-design-system/utils";

import { useChatStore } from "../store/chatStore";

interface ChatInputProps {
	readonly isDisabled: boolean;
	readonly isSubmitDisabled?: boolean;
	readonly onSubmit: (inputValue: string) => Promise<void>;
	readonly placeholder?: string;
	readonly className?: string;
	readonly textareaClassName?: string;
	readonly isFollowupActive?: boolean;
	readonly headerComponent?: ReactNode;
	readonly textareaEndComponent?: ReactNode;
	readonly footerLeftComponent?: ReactNode;
	readonly footerRightComponent?: ReactNode;
}

export default function ChatInput({
	isDisabled,
	isSubmitDisabled,
	onSubmit,
	placeholder,
	className,
	textareaClassName,
	isFollowupActive,
	headerComponent,
	textareaEndComponent,
	footerLeftComponent,
	footerRightComponent,
}: ChatInputProps) {
	const inputValue = useChatStore((store) => store.inputValue);
	const setInputValue = useChatStore((store) => store.setInputValue);
	const [isWaitingForResponse, setIsWaitingForResponse] = useState<boolean>(false);

	useEffect(() => {
		if (isDisabled) {
			setIsWaitingForResponse(false);
		}
	}, [isDisabled]);

	// eslint-disable-next-line @typescript-eslint/no-deprecated
	function handleSubmit(e?: FormEvent<HTMLFormElement> | undefined) {
		if (e) {
			e.preventDefault();
		}

		if (isDisabled || isSubmitDisabled || isWaitingForResponse) {
			return;
		}

		setIsWaitingForResponse(true);

		const currentValue = inputValue;

		setInputValue("");
		void onSubmit(currentValue.trim());
	}

	function handleKeyDown(event: React.KeyboardEvent<HTMLTextAreaElement>) {
		if ((event.metaKey || event.altKey || event.ctrlKey) && event.key === "Enter") {
			setInputValue(inputValue + "\n");
		} else if (event.shiftKey && event.key === "Enter") {
			return;
		} else if (event.key === "Enter") {
			event.preventDefault();
			handleSubmit();
		}
	}

	const placeholderText = isFollowupActive
		? "Ask a follow-up question"
		: (placeholder ??
			"Ask anything. Add a brain or drag & drop documents for grounded answers.");

	return (
		<div
			className={cn(
				"flex max-w-3xl flex-col rounded-2xl border border-secondary-border bg-secondary py-2 shadow-[0_0_10px_rgba(0,0,0,0.10)]",
				className,
			)}
		>
			{headerComponent}
			<form className="flex" onSubmit={handleSubmit}>
				<div className={cn("relative min-h-[60px] w-full px-6 py-2", textareaClassName)}>
					<Textarea
						value={inputValue}
						color="simple"
						rows={1}
						isClearable={false}
						classNames={{
							input: cn(
								"[field-sizing:content] max-h-[20dvh] resize-none border-0 bg-transparent p-0 focus-within:outline-0",
								textareaEndComponent && "pr-16",
							),
						}}
						placeholder={placeholderText}
						onValueChange={setInputValue}
						onKeyDown={handleKeyDown}
					/>
					{textareaEndComponent && (
						<div className="absolute right-1 bottom-[calc(50%-18px)]">
							{textareaEndComponent}
						</div>
					)}
				</div>
			</form>
			{(footerLeftComponent ?? footerRightComponent) && (
				<div className="@container/chat-input-footer flex justify-between gap-2 px-4 pt-2">
					<div className="flex w-full min-w-0 flex-row items-center gap-2">
						{footerLeftComponent}
					</div>
					<div className="flex items-center gap-2">{footerRightComponent}</div>
				</div>
			)}
		</div>
	);
}
